import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CubeIcon,
  PlusIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { v4 as uuidv4 } from "uuid";
import ModernProductSelectorDialog from "../../../utils/Selector/ModernProductSelectorDialog";
import { serviceAgreementIntervals } from "./serviceAgreementIntervals";

const input =
  "block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-50 disabled:text-slate-500";
const numberInput = `${input} text-right tabular-nums`;
const fieldLabel = "mb-1 block text-xs font-medium text-slate-500";
const ghostButton =
  "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";
const iconButton =
  "rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40";

const money = new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK" });

export const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const parsed = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : NaN;
};

const displayNumber = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed).replace(".", ",") : String(value);
};

export const lineTotal = (line) =>
  toNumber(line.quantity) * toNumber(line.sales_price) * (1 - toNumber(line.discount) / 100);

/** Lines as edited here: numbers as typed, stable key, deleted rows kept for the API. */
export function toEditableLines(orderlines = []) {
  return [...orderlines]
    .filter((line) => !line.group_row)
    .sort((a, b) => (a.row_order ?? 0) - (b.row_order ?? 0) || (a.id ?? 0) - (b.id ?? 0))
    .map((line) => ({
      ...line,
      key: line.id ? `id-${line.id}` : uuidv4(),
      item_type: line.item_type || (line.product?.id ? "product" : "service"),
      quantity: displayNumber(line.quantity, "1"),
      sales_price: displayNumber(line.sales_price, "0"),
      discount: displayNumber(line.discount, "0"),
      include_in_jobs: line.include_in_jobs !== false,
    }));
}

/** API payload for PUT /service/agreements/:id (update_items). */
export function toOrderlinesPayload(lines) {
  let order = 0;
  return lines
    .filter((line) => line.id || !line.delete)
    .map((line) => ({
      id: line.id || null,
      delete: line.delete === true,
      product_id: line.item_type === "product" ? line.product?.id || line.product_id || null : null,
      title: line.title || "",
      description: line.description || "",
      quantity: toNumber(line.quantity),
      sales_price: toNumber(line.sales_price),
      discount: toNumber(line.discount),
      unit_id: line.unit_id ?? line.product?.unit_id ?? null,
      group_row: false,
      item_type: line.item_type,
      interval_id: line.interval_id ?? null,
      include_in_jobs: line.include_in_jobs !== false,
      next_service_date: line.next_service_date || null,
      row_order: line.delete ? line.row_order ?? 0 : order++,
    }));
}

export function validateLines(lines, t) {
  const errors = {};
  lines.forEach((line) => {
    if (line.delete) return;
    const rowErrors = {};
    if (line.item_type === "product" && !(line.product?.id || line.product_id)) rowErrors.name = t("service.agreements.lines.error_product");
    if (line.item_type === "service" && !String(line.title || "").trim()) rowErrors.name = t("service.agreements.lines.error_title");
    if (!(toNumber(line.quantity) > 0)) rowErrors.quantity = t("service.agreements.lines.error_quantity");
    if (Number.isNaN(toNumber(line.sales_price)) || toNumber(line.sales_price) < 0) rowErrors.sales_price = t("service.agreements.lines.error_price");
    const discount = toNumber(line.discount);
    if (Number.isNaN(discount) || discount < 0 || discount > 100) rowErrors.discount = t("service.agreements.lines.error_discount");
    if (Object.keys(rowErrors).length) errors[line.key] = rowErrors;
  });
  return errors;
}

function FieldError({ message }) {
  return message ? <p className="mt-1 text-xs font-medium text-rose-600">{message}</p> : null;
}

function TypeSwitch({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const options = [
    { value: "service", label: t("service.agreements.lines.type_service"), Icon: WrenchScrewdriverIcon },
    { value: "product", label: t("service.agreements.lines.type_product"), Icon: CubeIcon },
  ];
  return (
    <div role="radiogroup" aria-label={t("service.agreements.lines.type")} className="inline-flex rounded-lg bg-slate-100 p-0.5">
      {options.map(({ value: option, label, Icon }) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          disabled={disabled}
          onClick={() => onChange(option)}
          className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition ${
            value === option ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  );
}

function LineCard({ line, index, count, perLine, errors = {}, disabled, onChange, onRemove, onMove, onPickProduct }) {
  const { t } = useTranslation();
  const [showDescription, setShowDescription] = useState(Boolean(line.description));
  const set = (patch) => onChange({ ...line, ...patch });
  const total = lineTotal(line);
  const productName = line.product?.displayTitle || line.product?.title || line.title;

  return (
    <li className={`rounded-2xl border bg-white p-4 shadow-sm transition sm:p-5 ${line.include_in_jobs ? "border-slate-200" : "border-dashed border-slate-300 bg-slate-50/60"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <TypeSwitch
            value={line.item_type}
            disabled={disabled}
            onChange={(item_type) => set(item_type === "service" ? { item_type, product_id: null, product: null } : { item_type })}
          />
          {!line.include_in_jobs ? (
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">{t("service.agreements.lines.contract_only")}</span>
          ) : null}
          {perLine && line.in_active_report ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{t("service.agreements.info.line_in_active_report")}</span>
          ) : perLine && line.due_next_visit ? (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">{t("service.agreements.info.line_next_visit")}</span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" className={iconButton} disabled={disabled || index === 0} onClick={() => onMove(-1)} aria-label={t("service.agreements.lines.move_up")}>
            <ArrowUpIcon className="h-4 w-4" />
          </button>
          <button type="button" className={iconButton} disabled={disabled || index === count - 1} onClick={() => onMove(1)} aria-label={t("service.agreements.lines.move_down")}>
            <ArrowDownIcon className="h-4 w-4" />
          </button>
          <button type="button" className={`${iconButton} hover:bg-rose-50 hover:text-rose-600`} disabled={disabled} onClick={onRemove} aria-label={t("service.agreements.lines.remove")}>
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-4">
        {line.item_type === "product" ? (
          <>
            <span className={fieldLabel}>{t("service.agreements.lines.product")}</span>
            <button
              type="button"
              disabled={disabled}
              onClick={onPickProduct}
              className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm shadow-sm hover:border-blue-400 ${errors.name ? "border-rose-400 bg-rose-50" : "border-slate-300 bg-white"}`}
            >
              <span className="min-w-0">
                <span className={`block truncate font-medium ${productName ? "text-slate-900" : "text-slate-400"}`}>
                  {productName || t("service.agreements.lines.pick_product")}
                </span>
                {line.product?.item_number ? <span className="block text-xs text-slate-500">{t("service.agreements.lines.item_number")} {line.product.item_number}</span> : null}
              </span>
              <span className="shrink-0 text-xs font-semibold text-blue-700">{productName ? t("service.agreements.lines.change") : t("service.agreements.lines.choose")}</span>
            </button>
          </>
        ) : (
          <label className="block">
            <span className={fieldLabel}>{t("service.agreements.lines.service_title")}</span>
            <input
              className={`${input} ${errors.name ? "border-rose-400 bg-rose-50" : ""}`}
              value={line.title || ""}
              maxLength={255}
              disabled={disabled}
              placeholder={t("service.agreements.lines.service_placeholder")}
              onChange={(event) => set({ title: event.target.value })}
            />
          </label>
        )}
        <FieldError message={errors.name} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="block">
          <span className={fieldLabel}>{t("service.agreements.lines.quantity")}</span>
          <input className={numberInput} inputMode="decimal" value={line.quantity ?? ""} disabled={disabled} onChange={(event) => set({ quantity: event.target.value })} />
          <FieldError message={errors.quantity} />
        </label>
        <label className="block">
          <span className={fieldLabel}>{t("service.agreements.lines.unit_price")}</span>
          <input className={numberInput} inputMode="decimal" value={line.sales_price ?? ""} disabled={disabled} onChange={(event) => set({ sales_price: event.target.value })} />
          <FieldError message={errors.sales_price} />
        </label>
        <label className="block">
          <span className={fieldLabel}>{t("service.agreements.lines.discount")}</span>
          <input className={numberInput} inputMode="decimal" value={line.discount ?? ""} disabled={disabled} onChange={(event) => set({ discount: event.target.value })} />
          <FieldError message={errors.discount} />
        </label>
        <div className="flex flex-col justify-end rounded-lg bg-slate-50 px-3 py-2 text-right">
          <span className="text-xs font-medium text-slate-500">{t("service.agreements.lines.total")}</span>
          <span className="text-base font-semibold tabular-nums text-slate-900">{Number.isNaN(total) ? "—" : money.format(total)}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2">
        <label className="block">
          <span className={fieldLabel}>{t("service.agreements.lines.interval")}</span>
          <select
            className={input}
            value={line.interval_id ?? ""}
            disabled={disabled}
            onChange={(event) => set({ interval_id: event.target.value === "" ? null : Number(event.target.value) })}
          >
            <option value="">{t("service.agreements.lines.follows_agreement")}</option>
            {serviceAgreementIntervals.map((row) => (
              <option key={row.id} value={row.value}>{t(row.label)}</option>
            ))}
          </select>
        </label>
        {perLine ? (
          <div>
            <label className="block">
              <span className={fieldLabel}>{t("service.agreements.lines.next_service_date")}</span>
              <input type="date" className={input} value={line.next_service_date || ""} disabled={disabled} onChange={(event) => set({ next_service_date: event.target.value || null })} />
            </label>
            {line.last_service_date ? (
              <p className="mt-1 text-xs text-slate-500">{t("service.agreements.info.line_last")}: {new Date(line.last_service_date).toLocaleDateString("nb-NO")}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800" title={t("service.agreements.lines.include_in_jobs_hint")}>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            checked={line.include_in_jobs}
            disabled={disabled}
            onChange={(event) => set({ include_in_jobs: event.target.checked })}
          />
          {t("service.agreements.lines.include_in_jobs")}
        </label>
        {!showDescription ? (
          <button type="button" disabled={disabled} className="text-xs font-semibold text-blue-700 hover:underline" onClick={() => setShowDescription(true)}>
            + {t("service.agreements.lines.add_description")}
          </button>
        ) : null}
      </div>

      {showDescription ? (
        <label className="mt-3 block">
          <span className={fieldLabel}>{t("service.agreements.lines.description")}</span>
          <textarea className={input} rows={2} value={line.description || ""} disabled={disabled} onChange={(event) => set({ description: event.target.value })} />
        </label>
      ) : null}
    </li>
  );
}

export function AgreementLinesEditor({ lines, onChange, intervalMode, onIntervalModeChange, errors = {}, disabled = false }) {
  const { t } = useTranslation();
  const [picker, setPicker] = useState(null);
  const perLine = intervalMode === "per_line";
  const visible = lines.filter((line) => !line.delete);
  const sum = visible.reduce((acc, line) => acc + (Number.isNaN(lineTotal(line)) ? 0 : lineTotal(line)), 0);
  const jobSum = visible.filter((line) => line.include_in_jobs).reduce((acc, line) => acc + (Number.isNaN(lineTotal(line)) ? 0 : lineTotal(line)), 0);

  const update = (key, next) => onChange(lines.map((line) => (line.key === key ? next : line)));
  const remove = (key) =>
    onChange(lines.flatMap((line) => (line.key !== key ? [line] : line.id ? [{ ...line, delete: true }] : [])));
  const move = (key, step) => {
    const order = visible.map((line) => line.key);
    const from = order.indexOf(key);
    const to = from + step;
    if (to < 0 || to >= order.length) return;
    [order[from], order[to]] = [order[to], order[from]];
    const byKey = Object.fromEntries(lines.map((line) => [line.key, line]));
    onChange([...order.map((k) => byKey[k]), ...lines.filter((line) => line.delete)]);
  };
  const add = (item_type, product = null) =>
    onChange([
      ...lines,
      {
        key: uuidv4(),
        id: null,
        item_type,
        title: product ? product.displayTitle || product.title || "" : "",
        description: "",
        product,
        product_id: product?.id || null,
        unit_id: product?.unit_id ?? null,
        quantity: 1,
        sales_price: product?.sales_price ?? 0,
        discount: 0,
        interval_id: null,
        include_in_jobs: true,
        next_service_date: null,
      },
    ]);
  const applyProduct = (product) => {
    if (!product?.id) return;
    if (picker === "new") add("product", product);
    else {
      const line = lines.find((row) => row.key === picker);
      if (line) {
        update(line.key, {
          ...line,
          product,
          product_id: product.id,
          unit_id: product.unit_id ?? line.unit_id,
          title: product.displayTitle || product.title || line.title,
          sales_price: toNumber(line.sales_price) > 0 ? line.sales_price : product.sales_price ?? 0,
        });
      }
    }
    setPicker(null);
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm" aria-label={t("service.agreements.lines.title")}>
      <header className="space-y-4 border-b border-slate-100 p-5">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{t("service.agreements.lines.title")}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-500">{t("service.agreements.lines.subtitle")}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl bg-slate-50 p-3 sm:flex-row sm:items-center sm:gap-4">
          <span className="shrink-0 text-sm font-medium text-slate-700">{t("service.agreements.form.interval_mode")}</span>
          <div role="radiogroup" aria-label={t("service.agreements.form.interval_mode")} className="inline-flex shrink-0 self-start rounded-lg bg-slate-200/70 p-0.5">
            {["agreement", "per_line"].map((mode) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={intervalMode === mode}
                disabled={disabled}
                onClick={() => onIntervalModeChange(mode)}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-semibold transition ${intervalMode === mode ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                {t(mode === "agreement" ? "service.agreements.form.interval_mode_agreement" : "service.agreements.form.interval_mode_per_line")}
              </button>
            ))}
          </div>
          <p className="text-xs leading-5 text-slate-500">{t(perLine ? "service.agreements.lines.mode_per_line_hint" : "service.agreements.lines.mode_agreement_hint")}</p>
        </div>
      </header>

      <div className="p-5">
        {visible.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 px-6 py-10 text-center">
            <p className="text-sm font-semibold text-slate-800">{t("service.agreements.lines.empty_title")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("service.agreements.lines.empty_body")}</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {visible.map((line, index) => (
              <LineCard
                key={line.key}
                line={line}
                index={index}
                count={visible.length}
                perLine={perLine}
                errors={errors[line.key]}
                disabled={disabled}
                onChange={(next) => update(line.key, next)}
                onRemove={() => remove(line.key)}
                onMove={(step) => move(line.key, step)}
                onPickProduct={() => setPicker(line.key)}
              />
            ))}
          </ul>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className={ghostButton} disabled={disabled} onClick={() => add("service")}>
            <PlusIcon className="h-4 w-4" /> {t("service.agreements.lines.add_service")}
          </button>
          <button type="button" className={ghostButton} disabled={disabled} onClick={() => setPicker("new")}>
            <PlusIcon className="h-4 w-4" /> {t("service.agreements.lines.add_product")}
          </button>
        </div>
      </div>

      <footer className="flex flex-col gap-2 rounded-b-2xl border-t border-slate-100 bg-slate-50 px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-end sm:gap-8">
        <span className="flex justify-between gap-3 text-slate-600 sm:block">
          {t("service.agreements.lines.sum_jobs")} <strong className="ml-2 tabular-nums text-slate-900">{money.format(jobSum)}</strong>
        </span>
        <span className="flex justify-between gap-3 text-slate-600 sm:block">
          {t("service.agreements.lines.sum_all")} <strong className="ml-2 text-base tabular-nums text-slate-900">{money.format(sum)}</strong>
        </span>
      </footer>

      {picker ? (
        <ModernProductSelectorDialog
          open
          multiple={false}
          selectedProducts={[]}
          onClose={() => setPicker(null)}
          onChange={(rows) => applyProduct(rows?.[0])}
        />
      ) : null}
    </section>
  );
}
